import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  nhAppMainPages,
  isRouteAccessible,
  isRouteSupportedAtScope,
} from '@pflegenavi/frontend/routing';
import {
  useCanLeaveNursingHomeScope,
  useEmployeeIsNursingHomeAdminOrManager,
} from './roleBasedRestrictions';
import { useIsNursingHomeScope, useScope } from './ScopeContext';

interface RouteAccessGuardProps {
  /**
   * Called when the page the app was opened on - a link, a bookmark - is not
   * available to the employee, who lands on the dashboard instead. Redirects
   * later on (a scope switch leaving the page unsupported) follow from what the
   * employee just did and are not reported.
   *
   * @example
   * <RouteAccessGuard onUnavailablePageInLink={() => enqueueSnackbar(message)} />
   */
  onUnavailablePageInLink?: () => void;
}

export function RouteAccessGuard({
  onUnavailablePageInLink,
}: RouteAccessGuardProps = {}): ReactElement {
  const location = useLocation();
  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();
  const canLeaveNursingHomeScope = useCanLeaveNursingHomeScope();
  const { kind, selectedNursingHome, isScopeRestored } = useScope();

  // Both checks below redirect, so neither may run on a half-known picture. The
  // stored scope is read back asynchronously and reads as `nursing_home` until
  // it lands, and the profile arrives over the network - acting before either
  // does would bounce an employee off a page they are allowed to see, and a
  // reload on such a page would always end up on the dashboard.
  const isKnown = isScopeRestored && canLeaveNursingHomeScope !== undefined;

  // The admin/manager role is held per nursing home, so it can only gate a
  // route while a single home is in view - and only once it is known which home
  // that is. Above nursing home scope the scope selector has already done the
  // gating: it offers only the legal entities the employee is admin or manager
  // in, and tenant scope only to a global tenant admin (ENG-2503).
  const lacksRole =
    kind === 'nursing_home' &&
    Boolean(selectedNursingHome) &&
    !isRouteAccessible(location.pathname, isAdminOrManager);

  // The dashboard is supported at every scope, so this cannot bounce.
  const unsupportedAtScope =
    Boolean(canLeaveNursingHomeScope) &&
    !isRouteSupportedAtScope(location.pathname, kind);

  const mustLeave = isKnown && (lacksRole || unsupportedAtScope);

  // The first decision is about the page the app was opened on; every later
  // one follows a navigation or scope switch inside the app.
  const isDecided =
    isKnown && (kind !== 'nursing_home' || Boolean(selectedNursingHome));
  const isFirstDecision = useRef(true);
  useEffect(() => {
    if (!isDecided || !isFirstDecision.current) {
      return;
    }
    isFirstDecision.current = false;
    if (mustLeave) {
      onUnavailablePageInLink?.();
    }
  }, [isDecided, mustLeave, onUnavailablePageInLink]);

  if (mustLeave) {
    return <Navigate to={nhAppMainPages.DASHBOARD} replace />;
  }

  return <Outlet />;
}

export function useCanAccessRoute(path: string): boolean {
  const isNursingHomeScope = useIsNursingHomeScope();
  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();

  // See the comment in RouteAccessGuard: the role only gates routes while a
  // single nursing home is in view.
  return !isNursingHomeScope || isRouteAccessible(path, isAdminOrManager);
}
